<script lang="ts" setup>
import {
  ClassroomEmailInfo,
  ClassroomEmailInfoRecipient,
} from "~/classes/models/classroom-emails/classroom-email-info.model";
import { ClassroomsService } from "~/classes/models/classrooms/classrooms.service";
import {
  StudentLinkedAccount,
  StudentLinkedAccounts,
} from "~/classes/models/studentLinkedAccounts/student-linked-account.model";
import { Student } from "~/classes/models/students/student.model";

import { Subscription } from "rxjs";
import { ClassroomEmail } from "~/classes/models/classroom-emails/classroom-email.model";

const props = defineProps({
  recipients: {
    type: Array as PropType<ClassroomEmailInfoRecipient[]>,
    required: true,
  },
  classroomId: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "addRecipient",
  "removeRecipient",
  "update:modelValue",
]);

const allStudentLinkedAccounts = ref<StudentLinkedAccounts>([]);
const isLoading = ref(true);
const routeClassroomId = ref<string | undefined>(undefined);
const subscription = ref<Subscription | undefined>();

onMounted(() => {
  if (props.classroomId) {
    subscription.value = ClassroomsService.streamAllStudentLinkedAccounts({
      classroomId: props.classroomId,
    }).subscribe((studentLinkedAccounts) => {
      allStudentLinkedAccounts.value = studentLinkedAccounts;
      isLoading.value = false;
    });
  }

  routeClassroomId.value =
    props.classroomId ?? (useRoute().params.classroomId as string | undefined);
});

onUnmounted(() => {
  subscription.value?.unsubscribe();
});

const useClassroomDataStore = useUserClassroomsDataStore();
const { visibleClassrooms, allStudents } = storeToRefs(useClassroomDataStore);

// Ok so what I want to do is just group them all

const groupedStudents = computed(() => {
  const classroomStudents = visibleClassrooms.value
    .map((classroom) => {
      return {
        id: classroom.id!,
        name: classroom.label,
        students: allStudents.value
          .filter(
            (student) =>
              student.classroomIds.includes(classroom.id!) &&
              student.isArchived != true
          )
          .map((student) => {
            return {
              student: student,
              linkedAccounts: allStudentLinkedAccounts.value.filter(
                (linkedAccount) => linkedAccount.studentId == student.id
              ),
            };
          })
          .filter((student) => {
            return student.linkedAccounts.length > 0;
          }),
      };
    })
    .filter((classroom) => {
      if (
        routeClassroomId.value != undefined &&
        routeClassroomId.value != classroom.id
      ) {
        return false;
      }

      return classroom.students.length > 0;
    });

  const studentAccounts = {} as {
    [key: string]: {
      email: string;
      type: string;
      label: string;
      id: string;
      studentId: string;
    }[];
  };

  for (const classroom of classroomStudents) {
    for (const student of classroom.students) {
      studentAccounts[student.student.name] = student.linkedAccounts.map(
        (account) => {
          return {
            id: account.id!,
            email: account.email,
            type: useCapitalizeFirstLetter(account.type),
            label: `${useCapitalizeFirstLetter(account.type)} | ${
              account.email
            }`,
            studentId: student.student.id!,
          };
        }
      );
    }
  }

  return studentAccounts;
});

const onUpdate = (value: string[]) => {
  const emialRecipients: ClassroomEmailInfoRecipient[] = value
    .map((id) => {
      const matchingOption = Object.values(groupedStudents.value)
        .flat()
        .find((option) => option.id == id);

      if (matchingOption == undefined) {
        return;
      }

      return {
        studentId: matchingOption.studentId,
        email: matchingOption.email,
        label: matchingOption.type,
        hasRead: false,
      };
    })
    .filter((r) => r != undefined);

  emit("update:modelValue", emialRecipients);
};

const selectedRecipients = computed(() => {
  return props.recipients
    .map((recipient) => {
      const matchingStudentEmail = Object.values(groupedStudents.value)
        .flat()
        .find(
          (option) =>
            option.email == recipient.email &&
            option.studentId == recipient.studentId
        );

      if (matchingStudentEmail == undefined) {
        return;
      }

      return matchingStudentEmail.id;
    })
    .filter((r) => r != undefined);
});
</script>

<template>
  <div>
    <BaseSelectInput
      :grouped-options="groupedStudents"
      :allow-add-all="true"
      :allow-multiple="true"
      :show-tags="false"
      :placeholder="isLoading ? 'Loading...' : 'Select Recipients'"
      :are-options-grouped="true"
      value-prop="id"
      label="label"
      :model-value="selectedRecipients"
      @update:modelValue="onUpdate"
      side="top"
    />
  </div>
  <!-- <BasePopover
    placement="auto-start"
    :arrow-padding="400"
    :disalbed="disabled ?? isLoading"
    trigger-class="w-full"
    :controller="dialogController"
    align="start"
  >
    <div
      class="w-full border text-input-1 border-medium-default rounded h-[42px] bg-surface flex flex-row items-center px-2 justify-between cursor-pointer hover:bg-light-default"
      :class="{
        '!bg-light-tint !text-[#666666] pointer-events-none': disabled,
      }"
    >
      <BaseIconChevronUpDown class="h-[24px] shrink-0 mr-2" />
      <div
        class="grow min-w-0 text-left overflow-hidden text-ellipsis whitespace-nowrap items-center justify-start leading-[24px] text-medium"
      >
        {{ isLoading ? "Loading..." : "Select Recipients..." }}
      </div>
    </div>
    <template #content>
      <span class="hidden"> {{ someRef }} </span>
      <div class="h-[300px] w-[500px] p-1 flex flex-col select-none">
        <div class="flex flex-col grow min-h-0 overflow-auto">
          <div
            v-for="classroom in groupedStudents"
            :key="classroom.id"
            class="mb-4"
          >
            <div
              class="flex flex-row items-center justify-between border-b pb-1 pl-2"
            >
              <h2 class="font-bold text-h5">
                {{ classroom.name }}
              </h2>
              <BaseOutlinedButton @click="addAll(classroom)" color="secondary">
                <div class="flex flex-row items-center">
                  <BaseIconAddCircle />
                  <span class="ml-1">Add All</span>
                </div>
              </BaseOutlinedButton>
            </div>
            <div
              v-for="student in classroom.students"
              :key="student.student.id"
              class="px-2"
            >
              <h2 class="my-1">
                {{ student.student.name }}
              </h2>
              <div
                v-for="linkdAccount in student.linkedAccounts"
                :key="`${linkdAccount.id}-${student.student.id}-${isEmailSelected(student.student.id!, linkdAccount.email)}`"
                class="flex flex-row items-center hover:bg-light-default py-1 rounded cursor-pointer px-2"
                @click="toggleSelection(student.student.id!, linkdAccount)"
              >
                <BaseCheckbox
                  class="mr-2 pointer-events-none"
                  :key="`${linkdAccount.id}-${student.student.id}-${isEmailSelected(student.student.id!, linkdAccount.email)}`"
                  :model-value="isEmailSelected(student.student.id!, linkdAccount.email)"
                  size="small"
                />
                <span class="capitalize"> {{ linkdAccount.type }} </span>
                <span class="mx-2"> | </span>
                <span>
                  {{ linkdAccount.email }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <BaseButton @click="dialogController.close" class="shrink-0">
          Done
        </BaseButton>
      </div>
    </template>
  </BasePopover> -->
</template>

<style scoped></style>
